import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { PerformancePoint } from '@ema-atr/shared';
import { signedMoney, time } from './Format';

export type CurveMode = 'pnl' | 'return';
const dateLabel = new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', month: 'numeric', day: 'numeric' });
const hourLabel = new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit', hour12: false });

export function PerformanceChart({ points, mode }: { points: PerformancePoint[]; mode: CurveMode }) {
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [selected, setSelected] = useState<number | null>(null);
  const gradientId = useId().replaceAll(':', '');
  useEffect(() => {
    if (!container.current) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(240, entry.contentRect.width));
    });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);
  const geometry = useMemo(() => {
    const height = width < 560 ? 280 : 340;
    const left = width < 560 ? 58 : 74;
    const right = width - 20;
    const top = 24;
    const bottom = height - 40;
    const values = points.map((point) => mode === 'pnl' ? Number(point.cumulativePnl) : point.returnRate ?? 0);
    let min = 0;
    let max = 0;
    for (const value of values) { min = Math.min(min, value); max = Math.max(max, value); }
    const padding = max === min ? 1 : (max - min) * 0.12;
    min -= padding;
    max += padding;
    const start = points[0]?.timestamp ?? 0;
    const end = points.at(-1)?.timestamp ?? start;
    const x = (timestamp: number) => end === start ? (left + right) / 2 : left + (timestamp - start) / (end - start) * (right - left);
    const y = (value: number) => bottom - (value - min) / (max - min) * (bottom - top);
    const coordinates = points.map((point, index) => ({ x: x(point.timestamp), y: y(values[index]!) }));
    const path = coordinates.map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ');
    const area = coordinates.length > 1 ? `${path} L${coordinates.at(-1)!.x},${y(0)} L${coordinates[0]!.x},${y(0)} Z` : '';
    const ticks = Array.from({ length: 5 }, (_, index) => min + (max - min) * index / 4);
    return { height, left, right, top, bottom, start, end, x, y, coordinates, path, area, ticks };
  }, [points, mode, width]);
  const activeIndex = selected === null ? null : Math.min(points.length - 1, selected);
  const active = activeIndex === null ? null : points[activeIndex];
  const activePoint = activeIndex === null ? null : geometry.coordinates[activeIndex];
  const color = Number(points.at(-1)?.cumulativePnl ?? 0) < 0 ? '#ff8b85' : '#5be0ad';
  const title = mode === 'pnl' ? '累计收益金额' : '收益率';
  const dateTickCount = geometry.start === geometry.end ? 1 : width < 560 ? 3 : 5;
  const selectAt = (clientX: number) => {
    if (!container.current || !points.length) return;
    const rect = container.current.getBoundingClientRect();
    const coordinate = (clientX - rect.left) / rect.width * width;
    const timestamp = geometry.start + (coordinate - geometry.left) / (geometry.right - geometry.left) * (geometry.end - geometry.start);
    let low = 0;
    let high = points.length - 1;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (points[middle]!.timestamp < timestamp) low = middle + 1;
      else high = middle;
    }
    const previous = Math.max(0, low - 1);
    setSelected(Math.abs(points[previous]!.timestamp - timestamp) < Math.abs(points[low]!.timestamp - timestamp) ? previous : low);
  };
  return <div className="performance-chart" ref={container}>
    {!points.length ? <div className="empty-state"><strong>暂无已平仓记录</strong><small>点击立即同步，导入历史仓位后查看曲线</small></div> : <>
      <svg width="100%" height={geometry.height} viewBox={`0 0 ${width} ${geometry.height}`} role="img" aria-label={`${title}折线图，${points.length} 个时间点。可使用左右方向键查看明细。`} tabIndex={0}
        onPointerMove={(event) => selectAt(event.clientX)} onPointerDown={(event) => selectAt(event.clientX)}
        onPointerLeave={() => { if (document.activeElement !== container.current?.querySelector('svg')) setSelected(null); }}
        onFocus={() => setSelected((current) => current ?? points.length - 1)} onBlur={() => setSelected(null)}
        onKeyDown={(event) => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape'].includes(event.key)) {
            event.preventDefault();
            if (event.key === 'Escape') setSelected(null);
            else if (event.key === 'Home') setSelected(0);
            else if (event.key === 'End') setSelected(points.length - 1);
            else setSelected(Math.max(0, Math.min(points.length - 1, (selected ?? points.length - 1) + (event.key === 'ArrowLeft' ? -1 : 1))));
          }
        }}>
        <title>{title}，按平仓时间累计</title>
        <defs><linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.2" /><stop offset="100%" stopColor={color} stopOpacity="0.02" /></linearGradient></defs>
        {geometry.ticks.map((value) => <g key={value}><line x1={geometry.left} x2={geometry.right} y1={geometry.y(value)} y2={geometry.y(value)} className="chart-gridline" /><text x={geometry.left - 10} y={geometry.y(value) + 4} textAnchor="end" className="chart-axis-label">{value.toLocaleString('zh-CN', { notation: 'compact', maximumFractionDigits: 1 })}{mode === 'return' ? '%' : ''}</text></g>)}
        <line x1={geometry.left} x2={geometry.right} y1={geometry.y(0)} y2={geometry.y(0)} className="chart-zero" />
        {geometry.area ? <path d={geometry.area} fill={`url(#${gradientId})`} /> : null}
        <path d={geometry.path} stroke={color} strokeWidth="2.5" fill="none" strokeLinejoin="round" />
        {geometry.coordinates.at(-1) ? <circle cx={geometry.coordinates.at(-1)!.x} cy={geometry.coordinates.at(-1)!.y} r="4" fill={color} /> : null}
        {Array.from({ length: dateTickCount }, (_, index) => {
          const ratio = dateTickCount === 1 ? 0.5 : index / (dateTickCount - 1);
          const timestamp = geometry.start + (geometry.end - geometry.start) * ratio;
          return <text key={index} x={geometry.left + (geometry.right - geometry.left) * ratio} y={geometry.height - 12} textAnchor={dateTickCount === 1 ? 'middle' : index === 0 ? 'start' : index === dateTickCount - 1 ? 'end' : 'middle'} className="chart-axis-label">{(geometry.end - geometry.start < 86400000 ? hourLabel : dateLabel).format(timestamp)}</text>;
        })}
        {activePoint ? <g><line x1={activePoint.x} x2={activePoint.x} y1={geometry.top} y2={geometry.bottom} className="chart-crosshair" /><circle cx={activePoint.x} cy={activePoint.y} r="5" fill={color} stroke="#111a21" strokeWidth="2" /></g> : null}
      </svg>
      {active && activePoint ? <div className="chart-tooltip" role="status" style={{ left: Math.max(8, Math.min(width - 232, activePoint.x - 112)) }}>
        <span>{time(new Date(active.timestamp).toISOString())}</span><strong>{signedMoney(active.cumulativePnl).text} <small>USDT</small></strong>
        <span>收益率 {active.returnRate == null ? '尚未设置本金' : `${active.returnRate > 0 ? '+' : ''}${active.returnRate.toFixed(2)}%`}</span>
        <span>累计平仓 {active.closedPositions} 笔</span>
      </div> : null}
      <p className="chart-instructions">按平仓时间累计 · 悬停、轻触或使用左右方向键查看明细</p>
    </>}
  </div>;
}
